import { MIN_CONVERSATION_SEARCH_LENGTH } from '@upright/common/chat/conversation-search';
import { IconButton } from '@upright/ui/common/generic/components/button';
import { Input } from '@upright/ui/common/generic/components/input';
import { Cross } from '@upright/ui/icons';
import InterfaceSearch from '@upright/ui/icons/img/streamline-mini-line/interface-essential/search/interface-search.svg';
import { useEffect, useRef, useState } from 'react';
import { useChatConversationList } from './chat-history-data';
import { ChatsList } from './chats-list';

/** How long typing pauses before the search runs, so a word is searched once rather than per letter. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The search field over every conversation and the list under it (PLT-7658), on the Chats page and
 * in the chat side panel's Chats view (PLT-7883). What is typed is a draft; it becomes the search
 * after a pause in typing, and below the two-character threshold the search is empty and the list
 * is the whole list.
 *
 * Where the search is kept is the caller's: the page keeps it in the URL so Back from an opened
 * chat returns to the results, while the panel sits over whatever page the reader is on and keeps
 * it for as long as the panel is open. The layout around the field and the list is the caller's
 * too, through the two class names.
 */
export function ChatsSearch({
  query,
  onSearch,
  focusKey,
  onOpenConversation,
  currentConversationId,
  fieldClassName,
  bodyClassName,
}: {
  query: string;
  onSearch: (query: string) => void;
  /** The field takes focus whenever this changes to a value other than `null`. */
  focusKey: string | null;
  onOpenConversation?: (id: string) => void;
  currentConversationId?: string | null;
  fieldClassName?: string;
  bodyClassName: string;
}) {
  const [draft, setDraft] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);
  const conversationListQuery = useChatConversationList();

  useEffect(() => {
    const next = searchFor(draft);
    if (next === query) {
      return undefined;
    }
    const timer = setTimeout(() => {
      onSearch(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [draft, query, onSearch]);

  useEffect(() => {
    if (focusKey !== null) {
      inputRef.current?.focus();
    }
  }, [focusKey]);

  const clear = () => {
    setDraft('');
    onSearch('');
  };

  // With no chats at all there is nothing to search, so the list is its empty state alone. While
  // the list is loading, or after it failed, the field stays: it is the main control.
  const hasNoChats = query === '' && conversationListQuery.data?.length === 0;

  return (
    <>
      {!hasNoChats && (
        <div className={fieldClassName}>
          <Input
            ref={inputRef}
            label="Search chats"
            isLabelVisuallyHidden
            placeholder="Search chats"
            startAdornment={InterfaceSearch}
            endAdornment={
              draft === '' ? undefined : (
                <IconButton
                  variant="text"
                  size="small"
                  icon={Cross}
                  aria-label="Clear search"
                  onPress={() => {
                    clear();
                    inputRef.current?.focus();
                  }}
                />
              )
            }
            value={draft}
            onChange={setDraft}
            onKeyDown={event => {
              if (event.key === 'Escape' && draft !== '') {
                clear();
                return;
              }
              // React Aria stops every key the field is given a handler for from propagating. An Esc
              // with nothing to clear belongs to what the field sits in: the side panel closes on it.
              event.continuePropagation();
            }}
          />
        </div>
      )}
      <div className={bodyClassName}>
        <ChatsList
          query={query}
          onClearSearch={clear}
          onOpenConversation={onOpenConversation}
          currentConversationId={currentConversationId}
        />
      </div>
    </>
  );
}

/** The search a draft makes: trimmed, and empty below the threshold the top bar search uses too. */
function searchFor(draft: string): string {
  const trimmed = draft.trim();
  return trimmed.length < MIN_CONVERSATION_SEARCH_LENGTH ? '' : trimmed;
}
